import { useEffect, useState } from 'react';
import { cn } from '~/lib/utils';
import { DotmSquare11 } from '~/components/ui/dotm-square-11';
import styles from './ThinkingIndicatorUI.module.css';

export interface ThinkingIndicatorUIProps {
  text?: string;
  /** Optional status copy to cycle while the agent is busy. The first entry is shown immediately. */
  rotationTexts?: readonly string[];
  rotationIntervalMs?: number;
  duration?: number;
  showDots?: boolean;
  showLoadingDots?: boolean;
  className?: string;
  /** Constrain to parent width and wrap long lines (e.g. chat cascade footer). */
  fillWidth?: boolean;
  /**
   * Tick a live elapsed timer next to the label. Off by default: it only makes sense where the indicator
   * marks real model work. The other call sites (landing demos, "Loading conversation") would be timing
   * nothing the user cares about.
   */
  showElapsed?: boolean;
  /**
   * Epoch ms the timed stretch began. Pass it when the indicator can unmount and remount inside one run
   * (the chat row yields whenever a tool widget is the streaming tail), otherwise the timer restarts
   * mid-turn. Omit it and the timer runs from mount.
   */
  startedAt?: number | null;
  /** Use a caller-owned clock when the indicator can unmount and remount during one run. */
  elapsedText?: string;
}

const formatElapsed = (ms: number) => {
  const total = ms / 1000;
  if (total < 60) {
    return `${total.toFixed(1)}s`;
  }
  return `${Math.floor(total / 60)}m ${(total % 60).toFixed(1)}s`;
};

/**
 * Elapsed since mount. The indicator mounts when the turn starts thinking and unmounts when it stops, so
 * mount time is the start of this spell. Measured against a fixed timestamp rather than by counting ticks,
 * because browsers throttle timers in a background tab and a tick counter would quietly run slow.
 */
const useElapsed = (enabled: boolean, startedAt?: number | null) => {
  const [ms, setMs] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const start = startedAt ?? Date.now();
    setMs(Math.max(0, Date.now() - start));
    const id = setInterval(() => setMs(Math.max(0, Date.now() - start)), 100);
    return () => clearInterval(id);
  }, [enabled, startedAt]);

  return ms;
};

export const ThinkingIndicatorUI = ({
  text = '',
  rotationTexts,
  rotationIntervalMs = 2600,
  duration = 3,
  showDots = true,
  showLoadingDots = true,
  fillWidth = false,
  showElapsed = false,
  startedAt = null,
  className,
  elapsedText,
}: ThinkingIndicatorUIProps) => {
  const [rotationIndex, setRotationIndex] = useState(0);

  useEffect(() => {
    if (!rotationTexts || rotationTexts.length < 2) {
      setRotationIndex(0);
      return;
    }
    const timer = window.setInterval(() => {
      setRotationIndex((current) => current + 1);
    }, rotationIntervalMs);
    return () => window.clearInterval(timer);
  }, [rotationTexts, rotationIntervalMs]);

  const rotatingText = rotationTexts?.length
    ? rotationTexts[rotationIndex % rotationTexts.length]
    : text;
  const displayText = rotatingText.replace(/\.+$/, '');
  const elapsedMs = useElapsed(showElapsed, startedAt);
  const displayElapsed = elapsedText ?? (showElapsed ? formatElapsed(elapsedMs) : null);
  const hasLabel = displayText.length > 0 || showLoadingDots;

  return (
    <div
      className={cn(
        styles.typingDotsContainer,
        fillWidth && styles.typingDotsContainerFill,
        className,
      )}
    >
      {showDots && (
        <span className={styles.thinkingMatrix} data-lead-icon>
          <DotmSquare11 size={16} dotSize={2} ariaLabel="Thinking" />
        </span>
      )}
      {hasLabel && (
        <span
          className={cn(styles.typingText, fillWidth && styles.typingTextFill)}
          style={{ animationDuration: `${duration}s` }}
        >
          {displayText}
          {showLoadingDots && (
            <span className={styles.loadingDots}>
              <span className={styles.loadingDot}>.</span>
              <span className={styles.loadingDot}>.</span>
              <span className={styles.loadingDot}>.</span>
            </span>
          )}
        </span>
      )}
      {displayElapsed !== null && <span className={styles.elapsed}>{displayElapsed}</span>}
    </div>
  );
};
